/**
 * MainLayout — 全局页面布局组件
 * - 顶部导航栏：Logo + 主菜单 + 通知/聊天/用户操作
 * - 内容区域：通过 <Outlet /> 渲染子路由页面
 * - 底部 Footer
 * - 响应式设计，最大宽度 1200px 居中
 */
import React, {useEffect} from 'react'
import {io} from 'socket.io-client'
import styles from './index.scss'
import {useAuthStore} from '@/stores/useAuthStore'
import {useSSE} from '@/hooks/useSSE'
import {chatApi} from '@/api/chat'
import {appConfig} from '@/config'
import {Outlet, useNavigate, useLocation} from 'react-router-dom'
import {useNotificationStore} from '@/stores/useNotificationStore'
import {useChatUnreadStore} from '@/stores/useChatUnreadStore'
import {getUserAvatarUrl, getUserInitial} from '@/utils/userDisplay'
import {Layout as AntLayout, Menu, Avatar, Badge, Dropdown, Space} from 'antd'
import {
  TeamOutlined,
  HomeOutlined,
  BellOutlined,
  UserOutlined,
  HeartOutlined,
  LogoutOutlined,
  SettingOutlined,
  MessageOutlined,
  AppstoreOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  SafetyOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons'

const {Header, Content, Footer} = AntLayout

const MainLayout: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const userInfo = useAuthStore((state) => state)
  const logout = useAuthStore((state) => state.logout)
  const userId = userInfo?.user?.id
  const isAdmin = userInfo?.isAdmin
  const unreadCount = useNotificationStore((s) => s.unreadCount)
  const setUnreadCount = useNotificationStore((s) => s.setUnreadCount)
  const refreshUnreadCount = useNotificationStore((s) => s.refreshUnreadCount)
  const chatUnreadCount = useChatUnreadStore((s) => s.unreadCount)
  const setChatUnreadSummary = useChatUnreadStore((s) => s.setUnreadSummary)
  const resetChatUnread = useChatUnreadStore((s) => s.reset)
  const {connect, disconnect} = useSSE()

  useEffect(() => {
    if (!userId) {
      disconnect()
      setUnreadCount(0)
      resetChatUnread()
      return
    }

    let cancelled = false
    refreshUnreadCount()
      .then(() => {
        if (!cancelled) connect({skipUnreadPreflight: true})
      })
      .catch(() => {
        if (!cancelled) setUnreadCount(0)
      })

    return () => {
      cancelled = true
      disconnect()
    }
  }, [connect, disconnect, refreshUnreadCount, resetChatUnread, setUnreadCount, userId])

  useEffect(() => {
    if (!userId) {
      resetChatUnread()
      return
    }

    let cancelled = false
    chatApi
      .getUnreadSummary()
      .then((result) => {
        if (!cancelled) setChatUnreadSummary(result)
      })
      .catch(() => {
        if (!cancelled) resetChatUnread()
      })

    const socket = io(appConfig.socket.url, {
      path: appConfig.socket.path,
      withCredentials: true,
      transports: ['websocket']
    })

    socket.on('chatUnread', (payload) => {
      setChatUnreadSummary(payload)
    })

    return () => {
      cancelled = true
      socket.disconnect()
    }
  }, [resetChatUnread, setChatUnreadSummary, userId])

  /** 顶部主导航菜单项 */
  const menuItems = [
    {key: '/', icon: <HomeOutlined />, label: '首页'},
    {key: '/cats', icon: <TeamOutlined />, label: '猫咪'},
    {key: '/products', icon: <ShoppingOutlined />, label: '商城'},
    {key: '/listings', icon: <HeartOutlined />, label: '领养'},
    {key: '/community', icon: <MessageOutlined />, label: '社区'}
  ]

  /** 用户头像下拉菜单项 */
  const userMenuItems = [
    {key: 'profile', icon: <UserOutlined />, label: '个人中心'},
    {key: 'direct-messages', icon: <MessageOutlined />, label: '我的私信'},
    {key: 'orders', icon: <AppstoreOutlined />, label: '我的订单'},
    {key: 'settings', icon: <SettingOutlined />, label: '设置'},
    {type: 'divider' as const},
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true
    }
  ]

  if (isAdmin) {
    userMenuItems.unshift({key: 'admin', icon: <SafetyCertificateOutlined />, label: '后台管理'})
  }

  /** 处理用户下拉菜单点击事件 */
  const handleUserMenu = async ({key}: {key: string}) => {
    if (key === 'logout') {
      await logout()
      navigate('/auth/login')
    } else if (key === 'profile') {
      navigate('/profile')
    } else if (key === 'orders') {
      navigate('/orders')
    } else if (key === 'direct-messages') {
      navigate('/chat?direct=1')
    } else if (key === 'admin') {
      navigate('/admin')
    }
  }

  return (
    <AntLayout className={styles.layout}>
      <Header className={styles.header}>
        {/* Logo 点击回首页 */}
        <div className={styles.logo} onClick={() => navigate('/')}>
          🐱 喵星球
        </div>
        {/* 主导航菜单，高亮当前路由 */}
        <Menu
          mode='horizontal'
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({key}) => navigate(key)}
          className={styles.menu}
        />
        {/* 右侧操作区：通知铃铛 + 聊天入口 + 用户头像 */}
        <Space className={styles.actions} size={16}>
          <Badge count={unreadCount} size='small'>
            <BellOutlined className={styles.icon} onClick={() => navigate('/notifications')} />
          </Badge>
          <Badge dot={chatUnreadCount > 0}>
            <MessageOutlined className={styles.icon} onClick={() => navigate('/chat')} />
          </Badge>
          {userId && <ShoppingCartOutlined className={styles.icon} onClick={() => navigate('/cart')} />}
          {userId ? (
            <Dropdown menu={{items: userMenuItems, onClick: handleUserMenu}} placement='bottomRight'>
              <Avatar className={styles.avatar} src={getUserAvatarUrl(userInfo.user)}>
                {getUserInitial(userInfo.user)}
              </Avatar>
            </Dropdown>
          ) : (
            <a onClick={() => navigate('/auth/login')}>登录</a>
          )}
        </Space>
      </Header>
      <Content className={styles.content}>
        <Outlet />
      </Content>
      <Footer className={styles.footer}>喵星球 Cat Planet ©2026</Footer>
    </AntLayout>
  )
}

export default MainLayout
